/**
 * Main Application Component
 */
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { AnimatePresence } from 'framer-motion';

import { store, persistor } from '@/store';
import { MainLayout } from '@/layouts/MainLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { ScrollToTop } from '@/components/common/ScrollToTop';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ROUTES } from '@/config/routes';
import toast from 'react-hot-toast';
import { onAuthStateChange, getOrCreateUserProfile, signOut, patchFooterLinks, getWishlistProductIds, getProductsByIds, setWishlistFB, getSavedForLaterProductIds, setSavedForLaterFB } from '@/services/firebase';
import { setUser, clearUser, setLoading } from '@/store/slices/authSlice';
import { clearWishlist, setWishlistItems, setWishlistLoading } from '@/store/slices/wishlistSlice';
import { clearSavedItems, clearCart, closeCartDrawer, setSavedForLaterItems, setSavedForLaterLoading, getSavedItemId, parseCartItemId } from '@/store/slices/cartSlice';
import { selectIsLoggingOut } from '@/store/slices/uiSlice';
import { fetchAllSettings } from '@/store/slices/settingsSlice';
import type { AppDispatch } from '@/store';
import { PageLoaderSkeleton } from '@/components/common/Skeleton';
import { LogoutLoader } from '@/components/common/LogoutLoader';

// Import global styles
import '@/styles/globals.css';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/Home'));
const ProductsPage = lazy(() => import('@/pages/Products'));
const ProductDetailPage = lazy(() => import('@/pages/ProductDetail'));
const CartPage = lazy(() => import('@/pages/Cart'));
const CheckoutPage = lazy(() => import('@/pages/Checkout'));
const OrdersPage = lazy(() => import('@/pages/Orders'));
const ProfilePage = lazy(() => import('@/pages/Profile'));
const WishlistPage = lazy(() => import('@/pages/Wishlist'));
const LoginPage = lazy(() => import('@/pages/Auth'));
const BrandsPage = lazy(() => import('@/pages/Brands'));
const ContactPage = lazy(() => import('@/pages/Contact'));
const AboutPage = lazy(() => import('@/pages/About'));
const TrackOrderPage = lazy(() => import('@/pages/TrackOrder'));

// Policy pages
const PrivacyPolicyPage = lazy(() => import('@/pages/Policies/PrivacyPolicy'));
const TermsOfServicePage = lazy(() => import('@/pages/Policies/TermsOfService'));
const ShippingPolicyPage = lazy(() => import('@/pages/Policies/ShippingPolicy'));
const ReturnsPolicyPage = lazy(() => import('@/pages/Policies/ReturnsPolicy'));

// Protected pages
const AddressesPage = lazy(() => import('@/pages/Addresses'));
const OrderSuccessPage = lazy(() => import('@/pages/OrderSuccess'));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetail'));

// Admin pages
const AdminPage = lazy(() => import('@/pages/Admin'));
const AdminDashboard = lazy(() => import('@/pages/Admin/Dashboard'));
const AdminProducts = lazy(() => import('@/pages/Admin/Products'));
const AdminProductForm = lazy(() => import('@/pages/Admin/Products/ProductForm'));
const AdminCategories = lazy(() => import('@/pages/Admin/Categories'));
const AdminCategoryForm = lazy(() => import('@/pages/Admin/Categories/CategoryForm'));
const AdminBrands = lazy(() => import('@/pages/Admin/Brands'));
const AdminBrandForm = lazy(() => import('@/pages/Admin/Brands/BrandForm'));
const AdminCoupons = lazy(() => import('@/pages/Admin/Coupons'));
const AdminCouponForm = lazy(() => import('@/pages/Admin/Coupons/CouponForm'));
const AdminUsers = lazy(() => import('@/pages/Admin/Users'));
const AdminSettings = lazy(() => import('@/pages/Admin/Settings'));
const AdminOrders = lazy(() => import('@/pages/Admin/Orders'));
const AdminOrderDetail = lazy(() => import('@/pages/Admin/Orders/OrderDetail'));
const AdminInquiries = lazy(() => import('@/pages/Admin/Inquiries'));

// Loading fallback component
const PageLoader: React.FC = () => <PageLoaderSkeleton />;

/**
 * AuthListener - Listens to Firebase auth state changes
 * Updates Redux store when user signs in/out
 */
const AuthListener: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // One-time patch for footer link paths, then fetch settings
    patchFooterLinks().then(() => dispatch(fetchAllSettings()));

    // Set loading state on mount
    dispatch(setLoading('loading'));

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userProfile = await getOrCreateUserProfile(
            firebaseUser.uid,
            (firebaseUser.phoneNumber || '').replace('+91', '')
          );

          // Block deactivated accounts before populating Redux (no flicker)
          if (userProfile.isActive === false) {
            await signOut();
            dispatch(clearUser());
            toast.error('Your account has been deactivated. Please contact support.');
            return;
          }

          if (userProfile) {
            dispatch(setUser(userProfile));

            // Merge local wishlist with Firebase wishlist
            dispatch(setWishlistLoading(true));
            try {
              const localItems = store.getState().wishlist.items;
              const localProductIds = localItems.map(item => item.product.id);
              const firebaseProductIds = await getWishlistProductIds(firebaseUser.uid);

              const mergedIds = [...new Set([...localProductIds, ...firebaseProductIds])];

              if (mergedIds.length > 0) {
                const needsSync = mergedIds.length !== firebaseProductIds.length ||
                  mergedIds.some(id => !firebaseProductIds.includes(id));
                if (needsSync) {
                  await setWishlistFB(firebaseUser.uid, mergedIds);
                }

                const products = await getProductsByIds(mergedIds);
                const wishlistItems = products.map(p => ({
                  product: p,
                  addedAt: localItems.find(li => li.product.id === p.id)?.addedAt || new Date().toISOString(),
                }));
                dispatch(setWishlistItems(wishlistItems));
              } else {
                dispatch(clearWishlist());
              }
            } catch (error) {
              console.error('Error syncing wishlist:', error);
            } finally {
              dispatch(setWishlistLoading(false));
            }

            // Merge local saved-for-later with Firebase saved-for-later
            dispatch(setSavedForLaterLoading(true));
            try {
              const localSaved = store.getState().cart.savedForLater;
              // Composite ids ("productId" or "productId::size") keep size variants distinct
              const localSavedIds = localSaved.map(item => getSavedItemId(item));
              const firebaseSavedIds = await getSavedForLaterProductIds(firebaseUser.uid);

              const mergedSavedIds = [...new Set([...localSavedIds, ...firebaseSavedIds])];

              if (mergedSavedIds.length > 0) {
                const needsSync = mergedSavedIds.length !== firebaseSavedIds.length ||
                  mergedSavedIds.some(id => !firebaseSavedIds.includes(id));
                if (needsSync) {
                  await setSavedForLaterFB(firebaseUser.uid, mergedSavedIds);
                }

                // Fetch the underlying products once per unique productId, then rebuild
                // each saved entry (a product may appear under multiple sizes).
                const parsedIds = mergedSavedIds.map(id => ({ id, ...parseCartItemId(id) }));
                const uniqueProductIds = [...new Set(parsedIds.map(p => p.productId))];
                const savedProducts = await getProductsByIds(uniqueProductIds);
                const productById = new Map(savedProducts.map(p => [p.id, p]));

                const savedItems = parsedIds
                  .map(({ id, productId, selectedSize }) => {
                    const product = productById.get(productId);
                    if (!product) return null;
                    return {
                      product,
                      selectedSize,
                      savedAt: localSaved.find(li => getSavedItemId(li) === id)?.savedAt || new Date().toISOString(),
                    };
                  })
                  .filter((item): item is NonNullable<typeof item> => item !== null);

                dispatch(setSavedForLaterItems(savedItems));
              } else {
                dispatch(clearSavedItems());
              }
            } catch (error) {
              console.error('Error syncing saved for later:', error);
            } finally {
              dispatch(setSavedForLaterLoading(false));
            }
          }
        } catch (error) {
          // Transient failure (e.g. network): keep a valid Firebase session
          // instead of logging the user out. Resolve loading from existing state.
          console.error('Error fetching user profile:', error);
          if (store.getState().auth.user) {
            dispatch(setLoading('succeeded'));
          } else {
            dispatch(setLoading('idle'));
          }
        }
      } else {
        dispatch(clearUser());
        dispatch(clearWishlist());
        dispatch(clearSavedItems());
        dispatch(clearCart());
        dispatch(closeCartDrawer());
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [dispatch]);

  return <>{children}</>;
};

/**
 * AppRoutes - Application routes configuration
 */
const AppRoutes: React.FC = () => (
  <Routes>
    {/* Main Layout Routes */}
    <Route element={<MainLayout />}>
      {/* Public Routes */}
      <Route path={ROUTES.HOME} element={<HomePage />} />
      <Route path={ROUTES.PRODUCTS} element={<ProductsPage />} />
      <Route path={ROUTES.PRODUCT_DETAIL} element={<ProductDetailPage />} />
      <Route path={ROUTES.CART} element={<CartPage />} />
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.BRANDS} element={<BrandsPage />} />
      <Route path={ROUTES.CONTACT} element={<ContactPage />} />
      <Route path={ROUTES.ABOUT} element={<AboutPage />} />
      <Route path={ROUTES.PRIVACY} element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.TERMS} element={<TermsOfServicePage />} />
      <Route path={ROUTES.SHIPPING} element={<ShippingPolicyPage />} />
      <Route path={ROUTES.RETURNS} element={<ReturnsPolicyPage />} />
      <Route path={ROUTES.ORDER_SUCCESS} element={<OrderSuccessPage />} />
      <Route path={ROUTES.TRACK_ORDER} element={<TrackOrderPage />} />

      {/* Protected Routes - Require Authentication */}
      <Route element={<ProtectedRoute />}>
        <Route path={ROUTES.CHECKOUT} element={<CheckoutPage />} />
        <Route path={ROUTES.ORDERS} element={<OrdersPage />} />
        <Route path={ROUTES.ORDER_DETAIL} element={<OrderDetailPage />} />
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
        <Route path={ROUTES.WISHLIST} element={<WishlistPage />} />
        <Route path={ROUTES.ADDRESSES} element={<AddressesPage />} />
      </Route>
    </Route>

    {/* Admin Routes - Require Admin Role */}
    <Route element={<ProtectedRoute requireAdmin />}>
      <Route element={<AdminLayout />}>
        <Route path={ROUTES.ADMIN} element={<AdminPage />} />
        <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
        <Route path={ROUTES.ADMIN_PRODUCTS} element={<AdminProducts />} />
        <Route path={ROUTES.ADMIN_PRODUCT_ADD} element={<AdminProductForm />} />
        <Route path={ROUTES.ADMIN_PRODUCT_EDIT} element={<AdminProductForm />} />
        <Route path={ROUTES.ADMIN_CATEGORIES} element={<AdminCategories />} />
        <Route path={ROUTES.ADMIN_CATEGORY_ADD} element={<AdminCategoryForm />} />
        <Route path={ROUTES.ADMIN_CATEGORY_EDIT} element={<AdminCategoryForm />} />
        <Route path={ROUTES.ADMIN_BRANDS} element={<AdminBrands />} />
        <Route path={ROUTES.ADMIN_BRAND_ADD} element={<AdminBrandForm />} />
        <Route path={ROUTES.ADMIN_BRAND_EDIT} element={<AdminBrandForm />} />
        <Route path={ROUTES.ADMIN_COUPONS} element={<AdminCoupons />} />
        <Route path={ROUTES.ADMIN_COUPON_ADD} element={<AdminCouponForm />} />
        <Route path={ROUTES.ADMIN_COUPON_EDIT} element={<AdminCouponForm />} />
        <Route path={ROUTES.ADMIN_ORDERS} element={<AdminOrders />} />
        <Route path={ROUTES.ADMIN_ORDER_DETAIL} element={<AdminOrderDetail />} />
        <Route path={ROUTES.ADMIN_USERS} element={<AdminUsers />} />
        <Route path={ROUTES.ADMIN_SETTINGS} element={<AdminSettings />} />
        <Route path={ROUTES.ADMIN_INQUIRIES} element={<AdminInquiries />} />
      </Route>
    </Route>
    
    {/* 404 Route */}
    <Route path="*" element={
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <h1>404 - Page Not Found</h1>
        <p>The page you're looking for doesn't exist.</p>
        <Link to={ROUTES.HOME}>Go Home</Link>
      </div>
    } />
  </Routes>
);

const GlobalLogoutLoader: React.FC = () => {
  const isLoggingOut = useSelector(selectIsLoggingOut);
  return <LogoutLoader isVisible={isLoggingOut} />;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={<PageLoader />} persistor={persistor}>
        <BrowserRouter>
          <AuthListener>
            <ScrollToTop />
            <AnimatePresence mode="wait">
              <Suspense fallback={<PageLoader />}>
                <AppRoutes />
              </Suspense>
            </AnimatePresence>
            <GlobalLogoutLoader />
          </AuthListener>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
};

export default App;
